import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchWallet, fetchWalletMovements, selectWallet } from '../../store/slices/walletSlice';
import { Wallet, WalletTransaction, CreditHistoryItem, walletService } from '../../services/api/walletService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number | string) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 })
    .format(Number(n));

const fmtDate = (d: string) =>
  new Date(d).toLocaleString('es-DO', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });

/** Primer día del mes actual YYYY-MM-DD */
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/** Hoy YYYY-MM-DD */
const todayISO = () => new Date().toISOString().split('T')[0];

// ─── Enums desde backend ──────────────────────────────────────────────────────

/**
 * WalletTransactionType — campo `type` del movimiento
 * Indica QUÉ operación lo originó.
 */
const OPERATION_OPTIONS = [
  { value: '',   label: 'Todas las operaciones' },
  { value: '0',  label: 'Balance' },
  { value: '1',  label: 'Recarga' },
  { value: '2',  label: 'Paquete' },
  { value: '3',  label: 'Factura' },
  { value: '4',  label: 'Seguro' },
  { value: '5',  label: 'Cargo servicio' },
  { value: '6',  label: 'Electricidad' },
  { value: '-1', label: 'Reversión' },
  { value: '-2', label: 'Wallet' },
  { value: '-3', label: 'Rev. cargo servicio' },
];

const OPERATION_LABEL: Record<number, string> = {
  0: 'Balance', 1: 'Recarga', 2: 'Paquete', 3: 'Factura', 4: 'Seguro',
  5: 'Cargo servicio', 6: 'Electricidad', [-1]: 'Reversión', [-2]: 'Wallet', [-3]: 'Rev. cargo servicio',
};

/**
 * WalletProccessType — campo `process` del movimiento
 * Indica CÓMO afectó la wallet.
 */
const PROCESS_OPTIONS = [
  { value: '',  label: 'Todos los procesos' },
  { value: '0', label: 'Crédito (ADD)' },
  { value: '1', label: 'Débito (SUBTRACT)' },
  { value: '2', label: 'Cancelación (CANCEL)' },
  { value: '3', label: 'Descuento (DISCOUNT)' },
  { value: '4', label: 'Pago (PAY)' },
  { value: '5', label: 'Deuda (ADDDEBT)' },
];

const PROCESS_INFO: Record<number, { label: string; color: string }> = {
  0: { label: 'Crédito',    color: '#10b981' },
  1: { label: 'Débito',     color: '#ef4444' },
  2: { label: 'Cancelación',color: '#f59e0b' },
  3: { label: 'Descuento',  color: '#2563eb' },
  4: { label: 'Pago',       color: '#ef4444' },
  5: { label: 'Deuda',      color: '#7c3aed' },
};

// ─── WalletType ───────────────────────────────────────────────────────────────

const walletKind = (w: Wallet): 'principal' | 'comision' | 'credito' => {
  const t = Number(w.is_primary);
  if (t === 0) return 'principal';
  if (t === 1) return 'comision';
  return 'credito';
};

const kindLabel = (w: Wallet | undefined): string => {
  if (!w) return '';
  const k = walletKind(w);
  if (k === 'principal') return 'Principal';
  if (k === 'comision')  return 'Comisión';
  return 'Crédito';
};

// ─── Tarjetas de wallet ───────────────────────────────────────────────────────

const CheckBadge = () => (
  <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center z-10"
    style={{ background: 'rgba(255,255,255,0.25)' }}>
    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
    </svg>
  </div>
);

const CardPrincipal: React.FC<{ w: Wallet; selected: boolean; onSelect: (id: string) => void }> = ({ w, selected, onSelect }) => (
  <button type="button" onClick={() => onSelect(String(w.id))}
    className="relative rounded-2xl p-6 text-white text-left transition-all duration-200 overflow-hidden"
    style={{
      background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
      boxShadow: selected ? '0 0 0 3px #2563eb, 0 8px 24px rgba(37,99,235,0.45)' : '0 4px 16px rgba(37,99,235,0.25)',
    }}>
    <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full opacity-20" style={{ background: 'rgba(255,255,255,0.3)' }} />
    <div className="absolute -right-2 bottom-2 w-20 h-20 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.3)' }} />
    {selected && <CheckBadge />}
    <div className="relative z-10">
      <p className="text-xs font-semibold uppercase tracking-widest opacity-70 mb-1">Wallet Principal</p>
      <p className="text-3xl font-black tracking-tight">{fmt(w.amount)}</p>
      <p className="text-xs opacity-50 mt-3">ID: {w.id}</p>
    </div>
  </button>
);

const CardCredito: React.FC<{ w: Wallet; selected: boolean; onSelect: (id: string) => void }> = ({ w, selected, onSelect }) => {
  const debtAmt = Number(w.debt ?? 0);
  const available = Number(w.amount);
  const limit = available + debtAmt;
  const usedPct = limit > 0 ? (debtAmt / limit) * 100 : 0;
  return (
    <button type="button" onClick={() => onSelect(String(w.id))}
      className="relative rounded-2xl p-6 text-white text-left transition-all duration-200 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)',
        boxShadow: selected ? '0 0 0 3px #06b6d4, 0 8px 24px rgba(6,182,212,0.45)' : '0 4px 16px rgba(6,182,212,0.25)',
      }}>
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full opacity-20" style={{ background: 'rgba(255,255,255,0.3)' }} />
      {selected && <CheckBadge />}
      <div className="relative z-10">
        <p className="text-xs font-semibold uppercase tracking-widest opacity-70 mb-1">Wallet Crédito</p>
        <p className="text-3xl font-black tracking-tight">{fmt(available)}</p>
        <div className="mt-3">
          <div className="flex justify-between text-xs opacity-75 mb-1">
            <span>Deuda actual: {fmt(debtAmt)}</span>
            <span>Disponible: {fmt(available)}</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
            <div className="h-1.5 rounded-full bg-white/80 transition-all" style={{ width: `${Math.min(usedPct, 100)}%` }} />
          </div>
          <p className="text-xs opacity-60 mt-1">{usedPct.toFixed(0)}% utilizado</p>
        </div>
        <p className="text-xs opacity-50 mt-2">ID: {w.id}</p>
      </div>
    </button>
  );
};

const CardComision: React.FC<{
  w: Wallet; selected: boolean;
  onSelect: (id: string) => void;
  onTransfer: () => void;
  transferring: boolean;
  transferLabel: string;
}> = ({ w, selected, onSelect, onTransfer, transferring, transferLabel }) => (
  <div
    className="relative rounded-2xl p-6 text-white text-left overflow-hidden"
    style={{
      background: 'linear-gradient(135deg, #92400e 0%, #f59e0b 100%)',
      boxShadow: selected ? '0 0 0 3px #f59e0b, 0 8px 24px rgba(245,158,11,0.45)' : '0 4px 16px rgba(245,158,11,0.25)',
    }}>
    <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full opacity-20" style={{ background: 'rgba(255,255,255,0.3)' }} />
    <div className="absolute -right-2 bottom-2 w-20 h-20 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.3)' }} />
    {/* Seleccionar wallet */}
    <button type="button" onClick={() => onSelect(String(w.id))}
      className="absolute inset-0 w-full h-full z-0" aria-label="Seleccionar wallet comisión" />
    {selected && <CheckBadge />}
    <div className="relative z-10">
      <p className="text-xs font-semibold uppercase tracking-widest opacity-70 mb-1">Wallet Comisión</p>
      <p className="text-3xl font-black tracking-tight">{fmt(w.amount)}</p>
      <p className="text-xs opacity-70 mt-2">Ganancias acumuladas</p>
      <p className="text-xs opacity-50 mt-1 mb-4">ID: {w.id}</p>
      {/* Botón transferir */}
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onTransfer(); }}
        disabled={transferring || Number(w.amount) <= 0}
        className="relative flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-50 active:scale-95"
        style={{ background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff' }}>
        {transferring
          ? <><svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Transfiriendo…</>
          : <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>Transferir a {transferLabel}</>
        }
      </button>
    </div>
  </div>
);

// ─── Página principal ─────────────────────────────────────────────────────────

const WalletPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    wallets, selectedWalletId, walletSelected,
    movements, movementsPage, movementsLastPage,
    isLoading, isLoadingMovements, error,
  } = useAppSelector((state) => state.wallet as any);

  // ── Tab activa cuando la wallet de crédito está seleccionada ──────────────
  const [activeTab, setActiveTab] = useState<'movimientos' | 'credito'>('movimientos');

  // ── Transferencia de comisiones ────────────────────────────────────────────
  const [transferring,    setTransferring]    = useState(false);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);
  const [transferError,   setTransferError]   = useState<string | null>(null);
  const [confirmTransfer, setConfirmTransfer] = useState(false);

  // ── Filtros de movimientos ─────────────────────────────────────────────────
  const [dateFrom,        setDateFrom]        = useState(firstOfMonth());
  const [dateTo,          setDateTo]          = useState(todayISO());
  const [filterType,      setFilterType]      = useState('');
  const [filterProcess,   setFilterProcess]   = useState('');

  // ── Estado del historial de crédito ───────────────────────────────────────
  const [creditHistory,     setCreditHistory]     = useState<CreditHistoryItem[]>([]);
  const [creditPage,        setCreditPage]        = useState(1);
  const [creditLastPage,    setCreditLastPage]    = useState(1);
  const [creditDebt,        setCreditDebt]        = useState<number>(0);
  const [loadingCredit,     setLoadingCredit]     = useState(false);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => { dispatch(fetchWallet()); }, [dispatch]);

  // ── Carga de movimientos con filtros ───────────────────────────────────────
  const loadMovements = useCallback((page = 1) => {
    if (!selectedWalletId) return;
    dispatch(fetchWalletMovements({
      id: selectedWalletId,
      page,
      filters: {
        from_date: dateFrom,
        to_date:   dateTo,
        ...(filterType    !== '' ? { type:    Number(filterType)    } : {}),
        ...(filterProcess !== '' ? { process: Number(filterProcess) } : {}),
      },
    }));
  }, [dispatch, selectedWalletId, dateFrom, dateTo, filterType, filterProcess]);

  useEffect(() => { loadMovements(1); }, [loadMovements]);

  // ── Carga del historial de crédito ─────────────────────────────────────────
  const loadCreditHistory = useCallback(async (page = 1) => {
    setLoadingCredit(true);
    try {
      const res = await walletService.getCreditHistory(page);
      if (res.success && res.data) {
        const { data, current_page, last_page, current_debt } = res.data;
        setCreditHistory(prev => page === 1 ? (data || []) : [...prev, ...(data || [])]);
        setCreditPage(current_page);
        setCreditLastPage(last_page);
        setCreditDebt(Number(current_debt) || 0);
      }
    } catch { /* silent */ }
    finally { setLoadingCredit(false); }
  }, []);

  // Recargar historial de crédito al cambiar de tab o seleccionar wallet de crédito
  const selectedWallet = wallets.find((w: Wallet) => String(w.id) === selectedWalletId);
  const isCreditWallet = selectedWallet && walletKind(selectedWallet) === 'credito';

  useEffect(() => {
    if (isCreditWallet && activeTab === 'credito') {
      loadCreditHistory(1);
    }
  }, [isCreditWallet, activeTab, loadCreditHistory]);

  // Reset tab al cambiar de wallet
  useEffect(() => {
    setActiveTab('movimientos');
  }, [selectedWalletId]);

  const handleSelect = (id: string) => {
    if (id === selectedWalletId) return;
    dispatch(selectWallet(id));
  };

  const handleTransferFunds = async () => {
    setTransferring(true);
    setTransferError(null);
    setTransferSuccess(null);
    setConfirmTransfer(false);
    try {
      const res = await walletService.transferWalletFunds();
      if (res.success) {
        setTransferSuccess(res.message || 'Comisiones transferidas a la wallet principal');
        dispatch(fetchWallet()); // refresca saldos
      } else {
        setTransferError(res.message || 'No se pudo realizar la transferencia');
      }
    } catch (e: any) {
      setTransferError(e.message || 'Error al transferir comisiones');
    } finally {
      setTransferring(false);
    }
  };

  const handleLoadMore = () => {
    if (movementsPage < movementsLastPage) loadMovements(movementsPage + 1);
  };

  // Clasificar wallets
  const principals  = wallets.filter((w: Wallet) => walletKind(w) === 'principal');
  const creditos    = wallets.filter((w: Wallet) => walletKind(w) === 'credito');
  const comisiones  = wallets.filter((w: Wallet) => walletKind(w) === 'comision');

  // Wallet destino de la transferencia: depende de walletSelected del servidor
  //   walletSelected === 1 → Crédito activa  (is_primary=2)
  //   walletSelected === 0 → Principal activa (is_primary=0)
  const destWallet: Wallet | undefined = walletSelected === 1 ? creditos[0] : principals[0];
  const destLabel = walletSelected === 1 ? 'Crédito' : 'Principal';

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (isLoading && wallets.length === 0) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded-xl w-48" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-40 bg-slate-200 rounded-2xl" />)}
          </div>
          <div className="h-64 bg-slate-200 rounded-2xl" />
        </div>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="p-5 max-w-5xl mx-auto animate-fadeIn">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center justify-center w-12 h-12 rounded-2xl text-white"
          style={{ background: 'linear-gradient(135deg, #2563eb, #06b6d4)', boxShadow: '0 4px 16px rgba(37,99,235,0.35)' }}>
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
          </svg>
        </div>
        <div>
          <h1 className="text-xl font-black text-slate-800">Mi Wallet</h1>
          <p className="text-sm text-slate-500">Administre su saldo y movimientos</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-5"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p className="text-sm font-semibold text-red-700">{error}</p>
        </div>
      )}

      {/* Wallet cards */}
      {wallets.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {principals.map((w: Wallet)  => <CardPrincipal key={w.id} w={w} selected={String(w.id) === selectedWalletId} onSelect={handleSelect} />)}
          {creditos.map((w: Wallet)    => <CardCredito   key={w.id} w={w} selected={String(w.id) === selectedWalletId} onSelect={handleSelect} />)}
          {comisiones.map((w: Wallet)  => <CardComision  key={w.id} w={w} selected={String(w.id) === selectedWalletId} onSelect={handleSelect} onTransfer={() => setConfirmTransfer(true)} transferring={transferring} transferLabel={destLabel} />)}
        </div>
      )}

      {wallets.length === 0 && !isLoading && (
        <div className="bg-white rounded-2xl shadow-card p-8 text-center mb-6">
          <p className="text-slate-500">No se encontraron wallets asociadas a su cuenta.</p>
        </div>
      )}

      {/* Feedback transferencia */}
      {transferSuccess && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4 animate-fadeIn"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-700">{transferSuccess}</p>
          </div>
          <button onClick={() => setTransferSuccess(null)} className="text-emerald-400 hover:text-emerald-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      )}
      {transferError && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4 animate-fadeIn"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">{transferError}</p>
          </div>
          <button onClick={() => setTransferError(null)} className="text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      )}

      {/* Modal confirmación de transferencia */}
      {confirmTransfer && (() => {
        const commWallet = comisiones[0] as Wallet | undefined;
        const destWalletModal = walletSelected === 1 ? creditos[0] as Wallet | undefined : principals[0] as Wallet | undefined;
        const destLabelModal = walletSelected === 1 ? 'Crédito' : 'Principal';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg,#92400e,#f59e0b)' }}>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-black text-slate-800">Transferir Comisiones</h3>
                    <p className="text-xs text-slate-400">Esta acción no se puede deshacer</p>
                  </div>
                </div>

                {/* Resumen */}
                <div className="rounded-xl overflow-hidden divide-y divide-slate-100 mb-4"
                  style={{ border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-xs font-semibold text-slate-500">Desde (Comisiones)</span>
                    <span className="text-sm font-black text-amber-600">{fmt(Number(commWallet?.amount ?? 0))}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-xs font-semibold text-slate-500">Hacia ({destLabelModal})</span>
                    <span className="text-sm font-bold text-slate-700">{fmt(Number(destWalletModal?.amount ?? 0))}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(37,99,235,0.04)' }}>
                    <span className="text-xs font-semibold text-slate-500">Nuevo saldo {destLabelModal}</span>
                    <span className="text-sm font-black text-blue-600">
                      {fmt(Number(destWalletModal?.amount ?? 0) + Number(commWallet?.amount ?? 0))}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-slate-400 text-center">
                  {`Se transferirá el saldo completo de comisiones a la wallet ${destLabelModal.toLowerCase()}.`}
                </p>
              </div>

              {/* Botones */}
              <div className="flex gap-3 px-6 pb-6">
                <button type="button" onClick={() => setConfirmTransfer(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 hover:bg-slate-50 transition-all">
                  Cancelar
                </button>
                <button type="button" onClick={handleTransferFunds} disabled={transferring}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#92400e,#f59e0b)', boxShadow: '0 4px 12px rgba(245,158,11,0.4)' }}>
                  {transferring
                    ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Procesando…</>
                    : <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>Confirmar</>
                  }
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Historial */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">

        {/* Cabecera con tabs si es wallet de crédito */}
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              {selectedWallet && (
                <p className="text-xs text-slate-400">
                  Wallet {kindLabel(selectedWallet)} — ID {selectedWallet.id}
                </p>
              )}
            </div>
            {(isLoadingMovements || loadingCredit) && (
              <svg className="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
            )}
          </div>

          {/* Tabs — solo aparecen si la wallet seleccionada es de crédito */}
          {isCreditWallet ? (
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-3 w-fit">
              {([
                { key: 'movimientos', label: 'Movimientos' },
                { key: 'credito',     label: 'Historial de Crédito' },
              ] as const).map(t => (
                <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === t.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          ) : (
            <h2 className="text-base font-bold text-slate-700 mb-3">Historial de Movimientos</h2>
          )}

          {/* Filtros — solo en tab de movimientos */}
          {activeTab === 'movimientos' && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Desde</span>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="px-3 py-2 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ background: '#f8fafc' }} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hasta</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="px-3 py-2 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ background: '#f8fafc' }} />
              </div>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="px-3 py-2 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ background: '#f8fafc' }}>
                {OPERATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={filterProcess} onChange={e => setFilterProcess(e.target.value)}
                className="px-3 py-2 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ background: '#f8fafc' }}>
                {PROCESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button type="button" onClick={() => loadMovements(1)} disabled={isLoadingMovements}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all"
                style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                Buscar
              </button>
              {(filterType !== '' || filterProcess !== '') && (
                <button type="button" onClick={() => { setFilterType(''); setFilterProcess(''); }}
                  className="px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:bg-red-50"
                  style={{ color: '#ef4444', border: '1px solid #fecaca' }}>
                  Limpiar filtros
                </button>
              )}
            </div>
          )}

          {/* Resumen deuda — solo en tab crédito */}
          {activeTab === 'credito' && isCreditWallet && (
            <div className="flex flex-wrap gap-4 mt-1">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.2)' }}>
                <svg className="w-4 h-4 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
                <span className="text-xs font-semibold text-slate-500">Deuda actual:</span>
                <span className="text-sm font-black text-cyan-700">{fmt(creditDebt)}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span className="text-xs font-semibold text-slate-500">Saldo disponible:</span>
                <span className="text-sm font-black text-emerald-700">{fmt(Number(selectedWallet?.amount ?? 0))}</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Tab: Movimientos ──────────────────────────────────────────────── */}
        {activeTab === 'movimientos' && (
          movements.length === 0 && !isLoadingMovements ? (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#f1f5f9' }}>
                <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <p className="text-slate-500 font-medium">No hay movimientos para los filtros seleccionados</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Fecha', 'Proceso', 'Operación', 'Monto', 'Saldo Final', 'Razón'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((mov: WalletTransaction, i: number) => {
                      const processInfo = PROCESS_INFO[mov.process] ?? { label: `Proc. ${mov.process}`, color: '#64748b' };
                      const opLabel     = OPERATION_LABEL[mov.type]  ?? `Op. ${mov.type}`;
                      const rawAmount   = Number(mov.amount);
                      const isPositive  = rawAmount >= 0;
                      const absAmount   = Math.abs(rawAmount);
                      return (
                        <tr key={mov.id || i} className="border-t border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-3 text-sm text-slate-500 whitespace-nowrap">{fmtDate(mov.date)}</td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
                              style={{ background: processInfo.color + '18', color: processInfo.color }}>
                              <span>{isPositive ? '↑' : '↓'}</span>
                              {processInfo.label}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm font-medium text-slate-700">{opLabel}</td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span className="text-sm font-bold" style={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                              {isPositive ? '+' : '-'}{fmt(absAmount)}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm font-medium text-slate-600 whitespace-nowrap">
                            {fmt(Number(mov.actual_amount))}
                          </td>
                          <td className="px-5 py-3 text-sm text-slate-500 max-w-xs truncate">{mov.reason || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between" style={{ background: '#f8fafc' }}>
                <p className="text-xs text-slate-400">
                  Mostrando <span className="font-bold text-slate-600">{movements.length}</span> movimientos
                </p>
                {movementsPage < movementsLastPage && (
                  <button type="button" onClick={handleLoadMore} disabled={isLoadingMovements}
                    className="px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 transition-all"
                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                    {isLoadingMovements ? 'Cargando…' : `Cargar más (pág. ${movementsPage}/${movementsLastPage})`}
                  </button>
                )}
              </div>
            </>
          )
        )}

        {/* ── Tab: Historial de Crédito ─────────────────────────────────── */}
        {activeTab === 'credito' && (
          creditHistory.length === 0 && !loadingCredit ? (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#f1f5f9' }}>
                <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
              </div>
              <p className="text-slate-500 font-medium">No hay historial de crédito disponible</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Fecha', 'Tipo', 'ID Transacción', 'Monto', 'Saldo Deuda'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {creditHistory.map((item: CreditHistoryItem, i: number) => {
                      // proccess: 0 = ADD (crédito otorgado), 4 = PAY (pago realizado)
                      const isCredit  = item.proccess === 0;
                      const rawAmount = Number(item.amount);
                      const absAmount = Math.abs(rawAmount);
                      return (
                        <tr key={item.xid || i} className="border-t border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-3 text-sm text-slate-500 whitespace-nowrap">{fmtDate(item.date)}</td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                              style={{
                                background: isCredit ? 'rgba(6,182,212,0.1)' : 'rgba(16,185,129,0.1)',
                                color:      isCredit ? '#0e7490'              : '#059669',
                              }}>
                              <span>{isCredit ? '↑' : '↓'}</span>
                              {isCredit ? 'Crédito otorgado' : 'Pago realizado'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs font-mono text-slate-400 truncate max-w-[160px]">{item.xid}</td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span className="text-sm font-bold" style={{ color: isCredit ? '#0e7490' : '#059669' }}>
                              {isCredit ? '+' : '-'}{fmt(absAmount)}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm font-medium text-slate-600 whitespace-nowrap">
                            {fmt(Number(item.actual_amount))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between" style={{ background: '#f8fafc' }}>
                <p className="text-xs text-slate-400">
                  Mostrando <span className="font-bold text-slate-600">{creditHistory.length}</span> registros
                </p>
                {creditPage < creditLastPage && (
                  <button type="button" onClick={() => loadCreditHistory(creditPage + 1)} disabled={loadingCredit}
                    className="px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 transition-all"
                    style={{ background: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc' }}>
                    {loadingCredit ? 'Cargando…' : `Cargar más (pág. ${creditPage}/${creditLastPage})`}
                  </button>
                )}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default WalletPage;
